/**
 * Script (PDF) bij een draaidag — de PL zet hem hier klaar, de crew opent hem met één tik
 * in FF Crew (Joris, 7 sep 2026). Opslag in blob via /Api/draaidagen-overzicht/{id}/bestanden;
 * de kijklink is publiek met een niet te raden naam, dus ook deelbaar met een freelancer.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';

const MAX_MB = 25;

export default function ScriptSectie({ draaidagId }) {
  const [bestanden, setBestanden] = useState(null);
  const [bezig, setBezig] = useState(false);
  const inputRef = useRef(null);

  const laad = useCallback(async () => {
    if (!draaidagId) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${draaidagId}/bestanden?soort=script`);
      const d = await leesJson(r, 'Script laden');
      setBestanden(d.bestanden || []);
    } catch (e) { setBestanden([]); meldFout(e, 'Script laden'); }
  }, [draaidagId]);
  useEffect(() => { setBestanden(null); laad(); }, [laad]);

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) { toast.error('Alleen PDF'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Groter dan ${MAX_MB} MB`); return; }
    setBezig(true);
    const id = toast.loading(`Script uploaden… (${Math.round(file.size / 1024)} kB)`);
    try {
      const data = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => rej(new Error('Bestand niet leesbaar')); fr.readAsDataURL(file); });
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${draaidagId}/bestanden`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ soort: 'script', naam: file.name, data, content_type: 'application/pdf' }),
      });
      await leesJson(r, 'Script uploaden');
      toast.success('Script staat klaar voor de crew', { id });
      await laad();
    } catch (e) { toast.error(e.message || 'Uploaden mislukt', { id }); }
    finally { setBezig(false); }
  };

  const verwijder = async (b) => {
    if (!(await bevestig({ titel: `Script "${b.naam}" verwijderen?`, tekst: 'De crew kan hem daarna niet meer openen in de app.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${draaidagId}/bestanden/script/${b.id}`, { method: 'DELETE' });
      await leesJson(r, 'Script verwijderen');
      toast.success('Script verwijderd');
      await laad();
    } catch (e) { meldFout(e, 'Script verwijderen'); }
  };

  const fmt = (iso) => { if (!iso) return ''; const d = new Date(iso); return `${d.getDate()}/${d.getMonth() + 1} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

  return (
    <Stack spacing={1}>
      {bestanden === null ? <CircularProgress size={16} /> : bestanden.length === 0 ? (
        <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>Nog geen script. Upload een PDF; de crew ziet hem direct in FF Crew.</Typography>
      ) : bestanden.map((b, i) => (
        <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" icon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />} label={b.naam} component="a" clickable
            href={`${BACKEND_URL}${b.url}`} target="_blank" rel="noopener"
            sx={{ fontWeight: 700, maxWidth: 300, bgcolor: i === 0 ? '#dcfce7' : undefined }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', flex: 1 }} noWrap>
            {i === 0 ? 'actueel · ' : 'ouder · '}{fmt(b.at)}{b.door ? ` · ${b.door.split('@')[0]}` : ''}
          </Typography>
          <Tooltip title="Verwijderen"><IconButton size="small" onClick={() => verwijder(b)}><DeleteOutlineIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></IconButton></Tooltip>
        </Box>
      ))}
      <Box>
        <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={bezig} onClick={() => inputRef.current?.click()}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {bestanden?.length ? 'Nieuwe versie uploaden' : 'Script uploaden (PDF)'}
        </Button>
        <input ref={inputRef} type="file" accept="application/pdf" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }} />
      </Box>
    </Stack>
  );
}
